import { useContext } from 'react';
import { UserContext } from '../App';
import { useSnackbar } from 'notistack';
import CustomError from '../components/CustomError/CustomError';
import { SELECTED_APPEARANCE } from '../constants/constants';
const CLEANUP_FLAG = 'sortCleanupDone';
const KEYS_TO_REMOVE = ['CORRELATIONS'];

export const getSelectedAppearance = email => {
  return localStorage.getItem(`${email}${SELECTED_APPEARANCE}`);
};

export const setSelectedAppearance = (email, value) => {
  return localStorage.setItem(`${email}${SELECTED_APPEARANCE}`, value);
};

export const LOCAL_STORAGE_CONSTANTS = {
  TABLE: {
    FILTER_VISIBILITY_VALUE: 'filtersVisibilityValue',
    SORT: 'sort',
    FIRST: 'first',
    COLUMN_ORDER: 'columnOrder',
    HIDDEN_COLUMN_NAMES: 'hiddenColumnNames'
  }
};

function removeSortByKeys(stored, keys) {
  const updated = { ...stored };

  for (const [tableName, tableSettings] of Object.entries(updated)) {
    if (tableSettings?.sort && keys.includes(tableSettings.sort.field)) {
      updated[tableName] = { ...tableSettings };
      delete updated[tableName].sort;
    }
  }

  return updated;
}

const useLocalStorageService = () => {
  const { email, currentOrgId } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const cleanOldSortFields = (keysToRemove = KEYS_TO_REMOVE) => {
    if (!keysToRemove?.length) return;

    const storageKey = getLocalStorageKey();
    if (!storageKey) return;

    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    const data = JSON.parse(raw);

    // If already cleaned, skip
    if (data[CLEANUP_FLAG]) return;

    const cleaned = removeSortByKeys(data, keysToRemove);

    // Mark cleanup as done
    cleaned[CLEANUP_FLAG] = true;

    localStorage.setItem(storageKey, JSON.stringify(cleaned));
  };

  const getLocalStorageKey = () => {
    if (!email || !currentOrgId) {
      enqueueSnackbar(<CustomError message={'User or organization information not found.'} />, {
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: null
      });
      return null;
    }
    return `${email}_${currentOrgId}`;
  };

  const getLocalStorageData = () => {
    const localStorageKey = getLocalStorageKey();
    if (!localStorageKey) return null;

    cleanOldSortFields();

    try {
      const data = localStorage.getItem(localStorageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      enqueueSnackbar(<CustomError message={`Error recovering data for: "${localStorageKey.split('_')[0]}": ${error}`} />, {
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: null
      });
      return {};
    }
  };

  const setLocalStorageData = data => {
    const localStorageKey = getLocalStorageKey();
    if (!localStorageKey) return;

    try {
      localStorage.setItem(localStorageKey, JSON.stringify(data));
    } catch (error) {
      enqueueSnackbar(<CustomError message={`Error saving data for: "${localStorageKey.split('_')[0]}": ${error}`} />, {
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: null
      });
    }
  };

  const getStorageKeyData = key => {
    const storageData = getLocalStorageData();
    return storageData ? storageData[key] : null;
  };

  const setStorageKeyData = (key, data) => {
    if (key == null) {
      console.log('Storage Key not found.');
      return;
    }
    const keyData = getLocalStorageData() || {};
    let dataToSave = { ...keyData[key] };
    dataToSave[data.key] = data.value;

    keyData[key] = dataToSave;
    setLocalStorageData(keyData);
  };

  const clearStorageKeyData = key => {
    const keyData = getLocalStorageData();
    if (keyData && key in keyData) {
      delete keyData[key];
      setLocalStorageData(keyData);
    }
  };

  const clearOrgStorageData = () => {
    const storageKey = getLocalStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  };

  const clearUserStorageData = () => {
    if (email) {
      let localStorageKeys = Object.keys(localStorage);

      localStorageKeys.forEach(localStorageKey => {
        if (localStorageKey.startsWith(email)) {
          localStorage.removeItem(localStorageKey);
        }
      });
    }
  };

  return {
    getLocalStorageData,
    setLocalStorageData,
    getStorageKeyData,
    setStorageKeyData,
    clearStorageKeyData,
    clearOrgStorageData,
    clearUserStorageData,
    cleanOldSortFields
  };
};

export default useLocalStorageService;
